import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { StatementPicker } from "./StatementPicker";
import { uploadFiles } from "@/lib/uploads";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AU_PHONE_RE = /^(?:\+?61|0)[2-478]\d{8}$/;
const normalizePhone = (v) => v.replace(/[\s()\-.]/g, "");

const NEXT_STEPS = [
  { n: "01", text: "We reply within one business day with your fee health check summary." },
  { n: "02", text: "A plain-English breakdown of every layer of fee you are currently paying." },
  { n: "03", text: "A no-obligation conversation about whether you are getting genuine value." },
];

const fieldClass =
  "w-full bg-transparent border-b border-white/25 focus:border-[#F9F8F4] outline-none py-3 text-base md:text-sm text-[#F9F8F4] placeholder:text-white/80 transition-[border-color] duration-300";

const labelClass = "block text-[10px] uppercase tracking-[0.25em] text-white mb-1";

export const FeeCheckForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    total_portfolio: "",
    investment_fees: "",
    platform_fees: "",
    num_accounts: "",
    adviser_fees: "",
    message: "",
    website: "",
  });
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!AU_PHONE_RE.test(normalizePhone(form.phone))) {
      toast.error("Please enter a valid Australian phone number.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/fee-checks`, { ...form, file_count: files.length });
      const { failed } = data.upload_token
        ? await uploadFiles("fee-checks", data.id, data.upload_token, files, setProgress)
        : { failed: 0 };
      toast.success("Request sent — we'll be in touch within one business day.");
      if (failed) toast.error(`${failed} file${failed > 1 ? "s" : ""} couldn't be uploaded — you can email them to hello@nuaprivatewealth.com.au.`);
      setFiles([]);
      setForm({ name: "", email: "", phone: "", total_portfolio: "", investment_fees: "", platform_fees: "", num_accounts: "", adviser_fees: "", message: "", website: "" });
    } catch (err) {
      toast.error("Your request couldn't be sent. Please try again in a moment.");
    } finally {
      setSending(false);
      setProgress(null);
    }
  };

  return (
    <section id="contact" data-testid="fee-check-form-section" className="bg-[#0A2333] py-16 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-14 md:gap-16">
        <div data-testid="fee-check-divider" aria-hidden="true" className="md:col-span-12 flex items-center justify-center gap-6 -mt-4 -mb-2">
          <span className="h-px w-16 md:w-28 bg-white/15" />
          <img src="/assets/nua-mark-transparent.png" alt="" className="h-9 w-auto opacity-90" />
          <span className="h-px w-16 md:w-28 bg-white/15" />
        </div>
        <div className="md:col-span-5">
          <Reveal>
            <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white">
              <span className="h-px w-10 bg-[#F9F8F4]/50" aria-hidden="true" />
              Request your fee health check
            </p>
            <h2 data-testid="fee-check-form-heading" className="mt-6 font-serif text-3xl md:text-4xl tracking-tight text-[#F9F8F4]">
              Find out what you're really paying.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-white max-w-md">
              Share what you know about your current fees — even rough figures help. We'll do
              the rest and show you exactly where you stand versus the market. It's complimentary
              and there's no obligation.
            </p>
          </Reveal>

          <div className="mt-12 space-y-8" data-testid="fee-check-next-steps">
            {NEXT_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={0.1 + i * 0.08}>
                <div className="flex gap-6 items-baseline border-t border-white/10 pt-6">
                  <span className="font-serif italic text-2xl text-[#A78558]">{s.n}</span>
                  <p className="text-sm leading-relaxed text-white">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.35}>
            <div className="mt-12 text-sm text-white space-y-1" data-testid="fee-check-form-contact">
              <p>hello@nuaprivatewealth.com.au</p>
              <p>0411 842 648</p>
              <p>Office 845, Level 8, 121 Marcus Clarke Street, Canberra ACT 2601</p>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          <Reveal delay={0.15}>
            <form data-testid="fee-check-form" onSubmit={submit} className="space-y-9">
              <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden" style={{ position: "absolute" }}>
                <label htmlFor="fee-check-website">Website</label>
                <input
                  id="fee-check-website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={update("website")}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-9">
                <div>
                  <label htmlFor="fee-check-name" className={labelClass}>Full name</label>
                  <input
                    id="fee-check-name"
                    data-testid="fee-check-name-input"
                    type="text"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Citizen"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="fee-check-email" className={labelClass}>Email</label>
                  <input
                    id="fee-check-email"
                    data-testid="fee-check-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@example.com.au"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="fee-check-phone" className={labelClass}>Phone</label>
                  <input
                    id="fee-check-phone"
                    data-testid="fee-check-phone-input"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="04 0000 0000"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="fee-check-portfolio" className={labelClass}>Total portfolio balance</label>
                  <input
                    id="fee-check-portfolio"
                    data-testid="fee-check-portfolio-input"
                    type="text"
                    required
                    value={form.total_portfolio}
                    onChange={update("total_portfolio")}
                    placeholder="e.g. $850,000"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="fee-check-accounts" className={labelClass}>Number of accounts</label>
                  <input
                    id="fee-check-accounts"
                    data-testid="fee-check-accounts-input"
                    type="text"
                    value={form.num_accounts}
                    onChange={update("num_accounts")}
                    placeholder="e.g. 2"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="fee-check-investment" className={labelClass}>Investment fees / MERs</label>
                  <input
                    id="fee-check-investment"
                    data-testid="fee-check-investment-input"
                    type="text"
                    value={form.investment_fees}
                    onChange={update("investment_fees")}
                    placeholder="e.g. 0.85% or not sure"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="fee-check-platform" className={labelClass}>Total platform fees</label>
                  <input
                    id="fee-check-platform"
                    data-testid="fee-check-platform-input"
                    type="text"
                    value={form.platform_fees}
                    onChange={update("platform_fees")}
                    placeholder="e.g. 0.30% or $1,200 p.a."
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="fee-check-adviser" className={labelClass}>Adviser / advice company fees</label>
                  <input
                    id="fee-check-adviser"
                    data-testid="fee-check-adviser-input"
                    type="text"
                    value={form.adviser_fees}
                    onChange={update("adviser_fees")}
                    placeholder="e.g. 0.66% or $3,300 p.a. or not sure"
                    className={fieldClass}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="fee-check-message" className={labelClass}>Anything else? (optional)</label>
                <textarea
                  id="fee-check-message"
                  data-testid="fee-check-message-input"
                  rows={3}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="For example, which super fund or platform you're with, or what prompted you to check."
                  className={`${fieldClass} resize-none`}
                />
              </div>
              <StatementPicker files={files} onChange={setFiles} onError={toast.error} progress={progress} />
              <div className="flex flex-wrap items-center gap-6">
                <button
                  data-testid="fee-check-submit-button"
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center gap-2 rounded-full bg-[#F9F8F4] px-8 py-4 text-xs uppercase tracking-[0.15em] text-[#0A2333] transition-[background-color,transform,opacity] duration-300 hover:bg-white hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {sending ? (
                    <>
                      {progress ? "Uploading" : "Sending"}
                      <Loader2 size={15} className="animate-spin" />
                    </>
                  ) : (
                    <>
                      Request my fee health check
                      <ArrowRight size={15} />
                    </>
                  )}
                </button>
                <p className="text-xs text-white max-w-xs">
                  No obligation. Your details are never shared or used for marketing.
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
